import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  deleteInstallmentPlanThunk,
  updateInstallmentPlanThunk,
} from "../../../store/thunks/branchInstallmentThunks";
import {
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/outline";
import EditInstallmentPlanModal from "../modals/EditInstallmentPlanModal";

const InstallmentPlansList = ({ branchCode, plans, isLoading }) => {
  const dispatch = useDispatch();
  const [editingPlan, setEditingPlan] = useState(null);
  const [deletingPlanId, setDeletingPlanId] = useState(null);

  const handleToggleActive = async (plan) => {
    if (
      window.confirm(
        `${plan.isActive ? "Deactivate" : "Activate"} plan "${plan.planName}"?`
      )
    ) {
      try {
        await dispatch(
          updateInstallmentPlanThunk({
            branchCode,
            planId: plan._id,
            updates: { isActive: !plan.isActive },
          })
        ).unwrap();
      } catch (error) {
        alert(`Failed to update plan: ${error}`);
      }
    }
  };

  const handleDelete = async (plan) => {
    const hardDelete = window.confirm(
      `Do you want to PERMANENTLY DELETE "${plan.planName}"?\n\n` +
        `Click OK to permanently delete, or Cancel to just deactivate.\n\n` +
        `Note: You cannot delete plans that have active orders.`
    );

    setDeletingPlanId(plan._id);
    try {
      await dispatch(
        deleteInstallmentPlanThunk({
          branchCode,
          planId: plan._id,
          hardDelete,
        })
      ).unwrap();

      alert(hardDelete ? "Plan deleted successfully" : "Plan deactivated");
    } catch (error) {
      if (error.activeOrders) {
        alert(
          `Cannot delete plan: ${error.activeOrders} active orders are using this plan`
        );
      } else {
        alert(`Failed to delete plan: ${error.message || error}`);
      }
    } finally {
      setDeletingPlanId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <p className="text-gray-600">No installment plans available</p>
        <p className="text-sm text-gray-500 mt-1">
          Create your first plan to start offering installments
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {plans.map((plan) => (
        <div
          key={plan._id}
          className={`bg-white border rounded-lg p-6 hover:shadow-md transition-shadow ${
            !plan.isActive ? "opacity-60" : ""
          }`}
        >
          <div className="flex justify-between items-start">
            <div className="flex-1">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <h4 className="text-xl font-bold text-gray-900">
                  {plan.planName}
                </h4>
                <span
                  className={`px-3 py-1 text-xs font-semibold rounded-full ${
                    plan.isActive
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {plan.isActive ? "Active" : "Inactive"}
                </span>
                {plan.requiresPremiumCustomer && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                    Premium Only
                  </span>
                )}
                {plan.requiresGuarantor && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                    Guarantor Required
                  </span>
                )}
              </div>

              {/* Plan Details Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-600">Tenure</p>
                  <p className="font-semibold text-lg">
                    {plan.tenureMonths} months
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Interest Rate</p>
                  <p className="font-semibold text-lg text-blue-600">
                    {plan.interestRate?.annual}% p.a.
                  </p>
                  <p className="text-xs text-gray-500">
                    ({plan.interestRate?.monthly}% monthly)
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Order Value Range</p>
                  <p className="font-semibold">
                    ₹{plan.minOrderValue?.toLocaleString()} - ₹
                    {plan.maxOrderValue?.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Down Payment</p>
                  <p className="font-semibold">
                    {plan.downPayment?.required
                      ? `${plan.downPayment.minPercentage}% min`
                      : "Not Required"}
                  </p>
                </div>
              </div>

              {/* Processing Fee */}
              {plan.processingFee?.amount > 0 && (
                <div className="bg-gray-50 p-3 rounded-lg mb-3">
                  <p className="text-sm text-gray-600">Processing Fee</p>
                  <p className="font-semibold">
                    {plan.processingFee.type === "PERCENTAGE"
                      ? `${plan.processingFee.amount}%`
                      : `₹${plan.processingFee.amount}`}
                    <span className="text-sm text-gray-600 ml-2">
                      ({plan.processingFee.charged})
                    </span>
                  </p>
                </div>
              )}

              {/* Benefits */}
              {plan.benefits && plan.benefits.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm text-gray-600 mb-2">Benefits:</p>
                  <div className="flex flex-wrap gap-2">
                    {plan.benefits.map((benefit, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full flex items-center gap-1"
                      >
                        <CheckCircleIcon className="h-3 w-3" />
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 ml-6">
              <button
                onClick={() => setEditingPlan(plan)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit Plan"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleToggleActive(plan)}
                className={`p-2 rounded-lg transition-colors ${
                  plan.isActive
                    ? "text-yellow-600 hover:bg-yellow-50"
                    : "text-green-600 hover:bg-green-50"
                }`}
                title={plan.isActive ? "Deactivate" : "Activate"}
              >
                {plan.isActive ? (
                  <XCircleIcon className="h-5 w-5" />
                ) : (
                  <CheckCircleIcon className="h-5 w-5" />
                )}
              </button>
              <button
                onClick={() => handleDelete(plan)}
                disabled={deletingPlanId === plan._id}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                title="Delete Plan"
              >
                {deletingPlanId === plan._id ? (
                  <div className="animate-spin h-5 w-5 border-2 border-red-600 border-t-transparent rounded-full"></div>
                ) : (
                  <TrashIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Edit Modal */}
      {editingPlan && (
        <EditInstallmentPlanModal
          branchCode={branchCode}
          plan={editingPlan}
          onClose={() => setEditingPlan(null)}
          onSuccess={() => {
            setEditingPlan(null);
          }}
        />
      )}
    </div>
  );
};

export default InstallmentPlansList;
